/**
 * ContextosIAManager.js
 *
 * Contextos de IA como sección propia del menú lateral (pedido de Angelo,
 * 2026-08-10): "que no necesitemos entrar a los workflows para cambiar el
 * contexto de la IA... un cliente real no puede hacer eso".
 *
 * El problema que resuelve: la base de conocimientos vivía DENTRO del nodo de
 * IA de cada workflow. Cambiarla obligaba a abrir Workflows, encontrar el
 * workflow correcto, encontrar el nodo, editar y guardar -- cuatro pasos que
 * asumen que quien los hace entiende el editor de flujos. Ahora el contexto es
 * una entidad con nombre propio (misma idea que las plantillas de mensaje): se
 * edita acá y todos los nodos que lo usan quedan actualizados al instante,
 * porque el motor lo resuelve en cada corrida (ver el case 'ai_reply' de
 * workflowEngine.js) en vez de copiarlo al guardar el paso.
 */

import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../utils/apiClient';
import { Lock, Plus, Trash2, Pencil, Sparkles, X } from 'lucide-react';
import CustomAlert from './shared/CustomAlert';

const MAX_LENGTH = 4000;

const ContextosIAManager = () => {
  const [contexts, setContexts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(true);
  const [editing, setEditing] = useState(null); // {id?, name, content}
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // {id, name, usage[]}

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/pos/crm/ai-contexts');
      setContexts(res.data || []);
      setHasAccess(true);
    } catch (err) {
      // Mismo gate que el resto del CRM: sin el módulo el backend responde 403
      // y se explica, en vez de mostrar una lista vacía que parece un bug.
      if (err.response?.status === 403) setHasAccess(false);
      else setAlert({ type: 'error', title: 'Error', message: 'No se pudieron cargar los contextos.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const name = (editing.name || '').trim();
    if (!name) {
      setAlert({ type: 'warning', title: 'Falta el nombre', message: 'Ponele un nombre para poder reconocerlo desde el nodo de IA.' });
      return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        await apiClient.put(`/api/pos/crm/ai-contexts/${editing.id}`, { name, content: editing.content || '' });
      } else {
        await apiClient.post('/api/pos/crm/ai-contexts', { name, content: editing.content || '' });
      }
      setEditing(null);
      await load();
      setAlert({ type: 'success', title: 'Guardado', message: 'Los workflows que usan este contexto ya están usando la versión nueva.' });
    } catch (err) {
      setAlert({ type: 'error', title: 'No se pudo guardar', message: err.response?.data?.error || 'Intentá de nuevo.' });
    } finally {
      setSaving(false);
    }
  };

  // Antes de borrar se consulta qué workflows lo usan: borrar a ciegas un
  // contexto que está en producción es justo lo que no queremos.
  const askDelete = async (ctx) => {
    let usage = [];
    try {
      const res = await apiClient.get(`/api/pos/crm/ai-contexts/${ctx.id}/usage`);
      usage = res.data || [];
    } catch (_) { /* informativo: si falla, se pregunta igual */ }
    setConfirmDelete({ id: ctx.id, name: ctx.name, usage });
  };

  const doDelete = async () => {
    try {
      await apiClient.delete(`/api/pos/crm/ai-contexts/${confirmDelete.id}`);
      setConfirmDelete(null);
      await load();
    } catch (err) {
      setConfirmDelete(null);
      setAlert({ type: 'error', title: 'No se pudo eliminar', message: err.response?.data?.error || 'Intentá de nuevo.' });
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Cargando contextos...</div>;

  if (!hasAccess) {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
        <Lock className="w-16 h-16 text-gray-300 mb-4" />
        <h2 className="text-xl font-bold text-gray-700 mb-2">Contextos de IA no está incluido en tu plan</h2>
        <p className="text-gray-500 max-w-md">Es parte del módulo CRM. Contacta a soporte para activarlo.</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" /> Contextos de IA
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Lo que la IA sabe de tu negocio. Editalo acá y los workflows que lo usan se actualizan solos, sin abrir ninguno.
          </p>
        </div>
        <button
          onClick={() => setEditing({ name: '', content: '' })}
          className="flex items-center gap-1.5 px-3 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 flex-shrink-0"
        >
          <Plus className="w-4 h-4" /> Nuevo contexto
        </button>
      </div>

      {contexts.length === 0 ? (
        <div className="border-2 border-dashed border-gray-200 rounded-xl p-10 text-center">
          <Sparkles className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600 font-semibold">Todavía no tenés ningún contexto</p>
          <p className="text-sm text-gray-400 mt-1 max-w-md mx-auto">
            Creá uno por cada servicio que atienda la IA (ej. "Peluquería", "Pet shop", "General"). Después, en el nodo de IA
            del workflow, elegís cuál usar.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {contexts.map((ctx) => (
            <div key={ctx.id} className="border border-gray-200 rounded-xl p-3 bg-white hover:border-purple-300 transition-colors">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-800 truncate">{ctx.name}</p>
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2 whitespace-pre-wrap">
                    {ctx.content || <span className="italic text-gray-400">Sin contenido</span>}
                  </p>
                  <p className="text-[10px] text-gray-400 mt-1">{(ctx.content || '').length} caracteres</p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => setEditing({ id: ctx.id, name: ctx.name, content: ctx.content || '' })}
                    className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg" title="Editar">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => askDelete(ctx)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Eliminar">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold text-gray-800">{editing.id ? 'Editar contexto' : 'Nuevo contexto'}</h3>
              <button onClick={() => setEditing(null)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>

            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Nombre</label>
            <input
              type="text"
              value={editing.name}
              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              placeholder="Ej: Peluquería"
              maxLength={120}
              className="w-full mt-1 mb-3 px-2 py-1.5 border rounded-lg text-sm"
            />

            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Qué debe saber la IA</label>
            <textarea
              value={editing.content}
              onChange={(e) => setEditing({ ...editing, content: e.target.value })}
              placeholder={'Ej: Sos el asistente de una veterinaria.\nHorario: lunes a sábado de 9 a 19.\nLa peluquería canina cuesta $25 y demora 2 horas.\nSi preguntan por urgencias, pedí que llamen al 099...'}
              rows={14}
              maxLength={MAX_LENGTH}
              className="w-full mt-1 px-2 py-1.5 border rounded-lg text-sm font-mono"
            />
            <div className="flex items-center justify-between mt-1">
              <p className="text-[10px] text-gray-400">
                Escribilo como se lo explicarías a un empleado nuevo. Cuanto más concreto, mejor responde.
              </p>
              <p className={`text-[10px] ${(editing.content || '').length > MAX_LENGTH - 200 ? 'text-amber-600 font-semibold' : 'text-gray-400'}`}>
                {(editing.content || '').length}/{MAX_LENGTH}
              </p>
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
              <button onClick={save} disabled={saving}
                className="px-4 py-2 text-sm font-semibold bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50">
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <CustomAlert
          type="warning"
          title={`¿Eliminar "${confirmDelete.name}"?`}
          message={
            confirmDelete.usage.length
              ? `Lo está usando: ${confirmDelete.usage.map(u => u.workflow_name).join(', ')}. Esos pasos van a pasar a usar el contexto general de la cuenta, no van a dejar de funcionar.`
              : 'No lo está usando ningún workflow.'
          }
          onConfirm={doDelete}
          onClose={() => setConfirmDelete(null)}
        />
      )}

      {alert && <CustomAlert {...alert} onClose={() => setAlert(null)} />}
    </div>
  );
};

export default ContextosIAManager;
